import React, { useContext } from "react";

import { CollisionPriority } from "@dnd-kit/abstract";
import { useSortable } from "@dnd-kit/react/sortable";

import { styled } from "simplydo/react/styled";

import { SortableDataContext } from "./SortableContext";

const DragHandler = styled.div<{ $isDragging: boolean }>`
  opacity: ${({ $isDragging }) => ($isDragging ? 0.3 : 1)};
  &[data-dnd-dragging] {
    opacity: 1;
  }
`;

type PriorityStr = keyof typeof CollisionPriority;

type DroppableProps = {
  children: React.ReactNode;
  id: string;
  type?: string;
  disabled?: boolean;
  index: number;
  accept?: string[];
  group?: string;
  style?: React.CSSProperties;
  handleRef?: React.RefObject<HTMLElement | null>;
  collisionPriority?: PriorityStr;
  attachToChild?: boolean;
};

const getCollisionPriority = (collisionPriority?: PriorityStr) => {
  switch (collisionPriority) {
    case "Normal":
      return CollisionPriority.Normal;
    case "Lowest":
      return CollisionPriority.Lowest;
    case "Low":
      return CollisionPriority.Low;
    case "High":
      return CollisionPriority.High;
    case "Highest":
      return CollisionPriority.Highest;
    default:
      return CollisionPriority.Normal;
  }
};

export const Sortable = ({
  children,
  id,
  type,
  disabled,
  index,
  accept,
  group,
  style,
  handleRef,
  collisionPriority,
  attachToChild,
}: DroppableProps) => {
  const { id: parentId } = useContext(SortableDataContext);

  const priority = getCollisionPriority(collisionPriority);
  const { ref, isDragging } = useSortable({
    id,
    index,
    type,
    group: group || parentId,
    collisionPriority: priority,
    accept,
    handle: handleRef,
    disabled: { draggable: !!disabled },
    data: { disabled },
  });

  // eslint-disable-next-line no-useless-assignment
  let dragHandler: React.ReactNode | null = null;
  if (attachToChild) {
    const onlyChild = React.Children.only(children);
    dragHandler = React.cloneElement(
      onlyChild as React.ReactElement<{ ref?: React.Ref<HTMLElement> }>,
      // Attach dnd-kit's sortable ref directly to the child so no wrapper
      // element is inserted (preserves styling hierarchy). Not reading ref.current.
      // eslint-disable-next-line react-hooks/refs -- cloneElement must merge the sortable ref onto the child
      { ref },
    );
  } else {
    dragHandler = (
      <DragHandler ref={ref} $isDragging={isDragging} style={style}>
        {children}
      </DragHandler>
    );
  }

  return (
    <SortableDataContext.Provider value={{ id }}>
      {dragHandler}
    </SortableDataContext.Provider>
  );
};
